import ApiServiceNode from '../service/ApiServiceNode';
import logger from '../utils/logger';



export const officeService = {
    // Office CRUD
    async getOffices({ page = 1, limit = 20, search = '' }) {
        try {
            const response = await ApiServiceNode.get('/settings/offices', {
                params: { page, limit, search }
            });
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching offices:', error);
            throw error;
        }
    },

    async getOffice(id) {
        try {
            const response = await ApiServiceNode.get(`/settings/offices/${id}`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching office:', error);
            throw error;
        }
    },

    async createOffice(data) {
        try {
            const response = await ApiServiceNode.post('/settings/offices', data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error creating office:', error);
            throw error;
        }
    },

    async updateOffice(id, data) {
        try {
            const response = await ApiServiceNode.put(`/settings/offices/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error updating office:', error);
            throw error;
        }
    },

    async getLeavePolicyVersions(officeId) {
        try {
            const response = await ApiServiceNode.get(`/settings/offices/${officeId}/leave-policy`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching leave policy versions:', error);
            throw error;
        }
    },

    async publishLeavePolicyVersion(officeId, data) {
        try {
            const response = await ApiServiceNode.post(`/settings/offices/${officeId}/leave-policy`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error publishing leave policy version:', error);
            throw error;
        }
    },

    async deleteOffice(id) {
        try {
            const response = await ApiServiceNode.delete(`/settings/offices/${id}`);
            return response.data;
        } catch (error) {
            logger.error('Error deleting office:', error);
            throw error;
        }
    },

    async getCurrencies() {
        try {
            const response = await ApiServiceNode.get('/common/currencies');
            return response.data.data?.currencies || response.data.currencies || [];
        } catch (error) {
            logger.error('Error fetching currencies:', error);
            throw error;
        }
    },

    // File upload/delete
    async uploadFile(file) {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await ApiServiceNode.post('/settings/offices/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            return response.data.data?.url || response.data.url;
        } catch (error) {
            logger.error('Error uploading file:', error);
            throw error;
        }
    },

    async deleteFiles(keys) {
        try {
            const response = await ApiServiceNode.delete('/settings/offices/files', {
                data: { keys }
            });
            return response.data;
        } catch (error) {
            logger.error('Error deleting files:', error);
            throw error;
        }
    },

    // Bank CRUD
    async getBanks(officeId) {
        try {
            const response = await ApiServiceNode.get(`/settings/offices/banks/${officeId}`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching banks:', error);
            throw error;
        }
    },

    async createBank(data) {
        try {
            const response = await ApiServiceNode.post('/settings/offices/banks', data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error creating bank:', error);
            throw error;
        }
    },

    async updateBank(id, data) {
        try {
            const response = await ApiServiceNode.put(`/settings/offices/banks/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error updating bank:', error);
            throw error;
        }
    },

    async deleteBank(id) {
        try {
            const response = await ApiServiceNode.delete(`/settings/offices/banks/${id}`);
            return response.data;
        } catch (error) {
            logger.error('Error deleting bank:', error);
            throw error;
        }
    },

    // Leave Type CRUD
    async getLeaveTypes(officeId) {
        try {
            const response = await ApiServiceNode.get(`/settings/offices/leave-types/${officeId}`);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error fetching leave types:', error);
            throw error;
        }
    },

    async createLeaveType(data) {
        try {
            const response = await ApiServiceNode.post('/settings/offices/leave-types', data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error creating leave type:', error);
            throw error;
        }
    },

    async updateLeaveType(id, data) {
        try {
            const response = await ApiServiceNode.put(`/settings/offices/leave-types/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error updating leave type:', error);
            throw error;
        }
    },

    async updateLeaveTypeSupportingDocument(id, data) {
        const response = await ApiServiceNode.patch(`/settings/offices/leave-types/${id}/supporting-document`, data);
        return response.data.data || response.data;
    },

    async deleteLeaveType(id) {
        try {
            const response = await ApiServiceNode.delete(`/settings/offices/leave-types/${id}`);
            return response.data;
        } catch (error) {
            logger.error('Error deleting leave type:', error);
            throw error;
        }
    },

    async toggleLeaveTypeActive(id, isActive) {
        try {
            const response = await ApiServiceNode.put(`/settings/offices/leave-types/${id}/active`, {
                is_active: isActive
            });
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error toggling leave type:', error);
            throw error;
        }
    },

    // Cost Rates
    async getCostRates(officeId) {
        try {
            const response = await ApiServiceNode.get(`/settings/offices/cost-rates/${officeId}`);
            // render() helper wraps data in { data: payload }
            return response.data.data || [];
        } catch (error) {
            logger.error('Error fetching cost rates:', error);
            throw error;
        }
    },

    async createCostRate(data) {
        try {
            const response = await ApiServiceNode.post('/settings/offices/cost-rates', data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error creating cost rate:', error);
            throw error;
        }
    },

    async updateCostRate(id, data) {
        try {
            const response = await ApiServiceNode.put(`/settings/offices/cost-rates/${id}`, data);
            return response.data.data || response.data;
        } catch (error) {
            logger.error('Error updating cost rate:', error);
            throw error;
        }
    },

    async deleteCostRate(id) {
        try {
            const response = await ApiServiceNode.delete(`/settings/offices/cost-rates/${id}`);
            return response.data;
        } catch (error) {
            logger.error('Error deleting cost rate:', error);
            throw error;
        }
    }
};
